import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import useUrlSearchParams from '@/hooks/useUrlSearchParams'
import { getCustomerServices } from '@/modules/Customer/services'
import {
    createServicesColumns,
    createServicesFilters,
    createSmartExpandedRow,
} from '@/modules/Customer/utils/customer-services-config'
import { getProductsGroupOptions } from '@/modules/ProductGroup/services'
import { useCallback, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

/**
 * Filters interface for Customer Services
 */
interface ServiceFilters {
    service_status: string[]
    product_group_name: string[]
}

interface UseCustomerServicesOptions {
    /** true = POC/Trial services, false = Official/Production services */
    isPoc: boolean
}

/**
 * Business logic hook for Customer Services (POC & Official)
 * Manages data fetching, pagination, sorting, and filtering
 */
export const useCustomerServices = ({ isPoc }: UseCustomerServicesOptions) => {
    const { id } = useParams()
    const { t } = useTranslation(['services', 'customer'])

    // URL params with prefix to avoid conflicts between POC and Official tables
    const prefix = isPoc ? 'poc' : ''
    const {
        params,
        filters,
        queryParams,
        setParams,
        setFilter,
        searchInputProps,
    } = useUrlSearchParams<ServiceFilters>({
        defaults: { sort_by: 'service_id', sort_order: 'desc', page_size: 10 },
        filterDefaults: {
            service_status: [],
            product_group_name: [],
        },
        prefix,
    })

    // Form for filter controls
    const { control } = useForm<ServiceFilters>({
        defaultValues: filters,
    })

    // Always inject is_poc filter
    const queryParamsWithFilter = useMemo(() => {
        let existingFilters = {}
        if (queryParams.filters) {
            try {
                existingFilters = JSON.parse(atob(queryParams.filters))
            } catch {
                // Ignore parse errors
            }
        }

        const mergedFilters = {
            ...existingFilters,
            is_poc: [isPoc],
        }

        // Clean empty values
        const cleanFilters: Record<string, unknown> = {}
        Object.entries(mergedFilters).forEach(([key, value]) => {
            if (value === undefined || value === null) return
            if (Array.isArray(value) && value.length === 0) return
            if (typeof value === 'string' && value === '') return
            if (typeof value === 'number' && value === 0) return
            cleanFilters[key] = value
        })

        return {
            ...queryParams,
            filters:
                Object.keys(cleanFilters).length > 0
                    ? btoa(JSON.stringify(cleanFilters))
                    : '',
        }
    }, [queryParams, isPoc])

    // Fetch services data
    const queryKeyType = isPoc ? 'poc' : 'official'
    const { data, renderState, isFetching } = useQueryWithHandler({
        queryKey: [
            QUERY_KEYS.SERVICES,
            queryKeyType,
            Number(id),
            queryParamsWithFilter,
        ],
        queryFn: () =>
            getCustomerServices({
                customerID: Number(id),
                ...queryParamsWithFilter,
            }),
        placeholderData: true,
    })

    // Fetch product groups (shared across both tables)
    const { data: productGroupOptions = [] } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.PRODUCT_GROUPS],
        queryFn: () => getProductsGroupOptions(),
        staleTime: 5 * 60 * 1000,
    })

    // Memoized columns & filters
    const columns = useMemo(() => createServicesColumns(t, isPoc), [t, isPoc])
    const filterItems = useMemo(
        () => createServicesFilters(t, productGroupOptions, isPoc),
        [t, productGroupOptions, isPoc]
    )

    // Smart expanded row - auto-selects template-specific renderer & expandable logic
    const smartExpandedRow = useMemo(() => createSmartExpandedRow(t), [t])
    const expandedRowRender = smartExpandedRow.render
    const rowExpandable = smartExpandedRow.isExpandable

    // Handlers
    const handleSortChange = useCallback(
        (sortBy: string, sortOrder: 'asc' | 'desc') =>
            setParams({ sort_by: sortBy, sort_order: sortOrder }),
        [setParams]
    )

    const handlePageChange = useCallback(
        (page: number) => setParams({ page }, false),
        [setParams]
    )

    const handlePageSizeChange = useCallback(
        (pageSize: number) => setParams({ page_size: pageSize, page: 1 }),
        [setParams]
    )

    return {
        // Data
        services: data?.results || [],
        pagination: data?.pagination,
        stateComponent: renderState(),
        isFetching,

        // Params & Filters
        params,
        control,
        setFilter,
        searchInputProps,

        // Configs
        columns,
        filterItems,
        expandedRowRender,
        rowExpandable,

        // Handlers
        handleSortChange,
        handlePageChange,
        handlePageSizeChange,

        // Translation
        t,
    }
}
